import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useAuth } from '@/hooks/useAuth';
import { StockProduct, StockStats, SizeQuantities, calculateTotalFromSizes, getDefaultSizeQuantities } from '@/types/stock';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export function useStockProducts() {
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [stats, setStats] = useState<StockStats>({
    totalProducts: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { activeOrganizationId } = useActiveOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const invalidateRelatedCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['stock-products'] });
    queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
    queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
  };

  const fetchProducts = useCallback(async () => {
    if (!activeOrganizationId) {
      setProducts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase
      .from('stock_products')
      .select(`
        *,
        category:stock_categories(*),
        supplier:stock_suppliers(*)
      `)
      .eq('organization_id', activeOrganizationId)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching stock products:', error);
      toast.error('Erro ao carregar produtos');
    } else {
      const productsData = (data || []).map((p: any) => ({
        ...p,
        size_quantities: p.size_quantities || getDefaultSizeQuantities(),
        current_quantity: calculateTotalFromSizes(p.size_quantities) || p.current_quantity || 0,
      })) as StockProduct[];
      setProducts(productsData);
      
      // Calculate stats
      const totalValue = productsData.reduce(
        (sum, p) => sum + (p.current_quantity * p.cost_price), 
        0
      );
      const lowStockCount = productsData.filter(
        p => p.current_quantity > 0 && p.current_quantity <= p.minimum_quantity
      ).length;
      const outOfStockCount = productsData.filter(
        p => p.current_quantity <= 0
      ).length;

      setStats({
        totalProducts: productsData.length,
        totalValue,
        lowStockCount,
        outOfStockCount,
      });
    }
    setIsLoading(false);
  }, [activeOrganizationId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const createProduct = async (data: {
    name: string;
    code?: string;
    size_quantities: SizeQuantities;
    minimum_quantity?: number;
    cost_price?: number;
    sale_price?: number;
  }) => {
    if (!activeOrganizationId || !user) return null;

    const totalQuantity = calculateTotalFromSizes(data.size_quantities);

    const { data: newProduct, error } = await supabase
      .from('stock_products')
      .insert([{
        organization_id: activeOrganizationId,
        name: data.name,
        code: data.code || null,
        size_quantities: data.size_quantities as any,
        current_quantity: totalQuantity,
        minimum_quantity: data.minimum_quantity || 0,
        unit: 'un',
        cost_price: data.cost_price || 0,
        sale_price: data.sale_price || null,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating product:', error);
      toast.error('Erro ao criar produto');
      return null;
    }

    toast.success('Produto criado com sucesso');
    fetchProducts();
    invalidateRelatedCaches();
    return newProduct;
  };

  const updateProduct = async (id: string, data: {
    name?: string;
    code?: string;
    size_quantities?: SizeQuantities;
    minimum_quantity?: number;
    cost_price?: number;
    sale_price?: number;
  }) => {
    const updateData: any = { ...data };
    
    if (data.size_quantities) {
      updateData.current_quantity = calculateTotalFromSizes(data.size_quantities);
    }

    const { error } = await supabase
      .from('stock_products')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Error updating product:', error);
      toast.error('Erro ao atualizar produto');
      return false;
    }

    toast.success('Produto atualizado');
    fetchProducts();
    invalidateRelatedCaches();
    return true;
  };

  const deleteProduct = async (id: string) => {
    const { error } = await supabase
      .from('stock_products')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
      return false;
    }

    toast.success('Produto excluído');
    fetchProducts();
    invalidateRelatedCaches();
    return true;
  };

  const getLowStockProducts = () => {
    return products.filter(p => p.current_quantity <= p.minimum_quantity && p.is_active);
  };

  return {
    products,
    stats,
    isLoading,
    createProduct,
    updateProduct,
    deleteProduct,
    getLowStockProducts,
    refreshProducts: fetchProducts,
  };
}
